import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { FileCode2, ChevronLeft, Search, RefreshCw, FileText, ShieldAlert } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function ManualTecnicoPage() {
  const { user } = useAuth();
  const [index, setIndex] = useState(null);
  const [activo, setActivo] = useState(null);
  const [contenido, setContenido] = useState("");
  const [q, setQ] = useState("");
  const [cargando, setCargando] = useState(true);
  const [estado, setEstado] = useState("ok"); // ok | forbidden | error

  const esAdmin = user && user.role === "admin";

  useEffect(() => {
    if (!esAdmin) { setCargando(false); return; }
    api.get("/manual-tecnico/index")
      .then((r) => {
        setIndex(r.data);
        const first = (r.data.capitulos || [])[0];
        if (first) setActivo(first);
      })
      .catch((e) => setEstado(e?.response?.status === 403 ? "forbidden" : "error"))
      .finally(() => setCargando(false));
  }, [esAdmin]);

  useEffect(() => {
    if (!activo) return;
    setContenido("");
    api.get(`/manual-tecnico/capitulo/${activo.archivo}`, { responseType: "text" })
      .then((r) => setContenido(typeof r.data === "string" ? r.data : String(r.data)))
      .catch(() => setContenido("_No se pudo cargar este capítulo._"));
  }, [activo]);

  // Autorización cliente: si NO es admin, no se muestra ninguna información parcial.
  if (user && user !== false && !esAdmin) {
    return <Navigate to="/" replace />;
  }

  const caps = (index?.capitulos || []).filter(
    (c) => !q.trim() || c.titulo.toLowerCase().includes(q.trim().toLowerCase())
  );

  return (
    <div className="px-5 md:px-8 py-8 max-w-6xl mx-auto" data-testid="manual-tecnico-page">
      <Link to="/centro-ayuda" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800">
        <ChevronLeft size={16} /> Volver al Centro de Ayuda
      </Link>

      <header className="mt-4 rounded-3xl overflow-hidden border border-slate-800 bg-gradient-to-br from-[#0B1220] to-[#111827] text-white p-8 md:p-10 relative">
        <div className="absolute -right-8 -top-8 opacity-10"><FileCode2 size={200} /></div>
        <p className="text-xs font-bold uppercase tracking-widest text-emerald-300">Documentación técnica · solo administración</p>
        <h1 className="mt-2 text-3xl md:text-4xl font-extrabold tracking-tight inline-flex items-center gap-3">
          <FileCode2 size={34} className="text-emerald-300" /> Manual Técnico
        </h1>
        <p className="mt-3 max-w-2xl text-slate-300 text-base">
          Documentación del código de ConstruLink, sincronizada automáticamente con el proyecto (endpoints, módulos, rutas, variables de entorno y dependencias).
        </p>
        {index?.generado_en && (
          <p className="mt-3 inline-flex items-center gap-2 text-xs text-slate-400" data-testid="manual-tecnico-generado">
            <RefreshCw size={13} /> Última generación: {index.generado_en}
          </p>
        )}
      </header>

      {cargando && <p className="mt-8 text-slate-400">Cargando documentación…</p>}

      {estado === "forbidden" && (
        <div className="mt-8 rounded-2xl border border-red-200 bg-red-50 p-6 flex items-start gap-3" data-testid="manual-tecnico-403">
          <ShieldAlert size={22} className="text-red-500 flex-shrink-0" />
          <div><p className="font-bold text-red-700">Acceso restringido</p>
            <p className="text-sm text-red-600">Este contenido es exclusivo del equipo de administración.</p></div>
        </div>
      )}
      {estado === "error" && (
        <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800" data-testid="manual-tecnico-vacio">
          Aún no se ha generado el Manual Técnico. Ejecuta <code>docsync init</code> para generarlo.
        </div>
      )}

      {estado === "ok" && index && (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8 items-start">
          <aside className="lg:sticky lg:top-6" data-testid="manual-tecnico-toc">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-4">
              <div className="relative mb-3">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="manual-tecnico-buscar" placeholder="Buscar capítulo…"
                  className="w-full rounded-xl border border-slate-200 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
              </div>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400 px-2 mb-1">Capítulos</p>
              <nav className="space-y-0.5">
                {caps.map((c) => (
                  <button key={c.id} onClick={() => setActivo(c)} data-testid={`manual-tecnico-cap-${c.id}`}
                    className={`w-full text-left px-2 py-1.5 rounded-lg text-sm transition-colors flex items-center gap-2 ${activo?.id === c.id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
                    <FileText size={14} className="flex-shrink-0 opacity-70" />
                    <span>{c.orden}. {c.titulo}</span>
                  </button>
                ))}
                {caps.length === 0 && <p className="px-2 py-2 text-sm text-slate-400">Sin resultados.</p>}
              </nav>
            </div>
          </aside>

          <article className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6 md:p-8 min-w-0" data-testid="manual-tecnico-contenido">
            <div className="cl-md">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{contenido}</ReactMarkdown>
            </div>
          </article>
        </div>
      )}
    </div>
  );
}
